import React, {useEffect, useMemo, useState} from 'react'
import {Box, Text, Image, Link} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useParams, Link as RouteLink} from 'react-router-dom'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import {BlogPageStyle} from './styles'
import LoadingSpinner from '../../components/loading-spinner'
import {FormattedMessage} from 'react-intl'
import useBlogData from '../../hooks/use-blog-data'
import {pxToRem} from '../../utills/pxToRem'
import {fonts} from '../../theme/foundations/fonts'
import FeaturedArticle from './FeaturedArticle'
import PopularPosts from './PopularPosts'
import Articles from './Articles'
import {sanitizeBlogSearchParams} from '../../utills/url'
import useFetchLibraryFolders from '../../hooks/useFetchLibrary'
import BlogArchive from './archive'
import {blogRoute} from '../../utills/url'
import ArticlesPagination from './ArticlesPagination'
import {DEFAULT_BLOGS_SEARCH_PARAMS} from '../../constants'
import {useToast} from '../../hooks/use-toast'
import {Spinner, useBreakpointValue} from '@chakra-ui/react'

const BlogLanding = () => {
    const [isOpenAllCAtegoryList, setIsOpenAllCAtegoryList] = useState(false)
    const [archiveFilters, setArchiveFilters] = useState({selectedYear: '', selectedMonth: ''})
    const {folderId} = useParams()
    const searchParams = useSearchParams(DEFAULT_BLOGS_SEARCH_PARAMS)
    const {sz, start, sort} = sanitizeBlogSearchParams(searchParams[0])
    const showToast = useToast()

    const folderResult = useFetchLibraryFolders(`/folders/BlogLink-cavenders-ranch`)
    const {data: {folders, ...rest} = {}} = folderResult || {}
    useEffect(() => {
        return setIsOpenAllCAtegoryList(false)
    }, [])

    const selectedCategoryId = useMemo(() => {
        if (!folders) return null
        const selectedFolder = folders?.filter((ddd) => ddd.c_href === `/blog/${folderId}`)
        if (selectedFolder && selectedFolder.length > 0) {
            return selectedFolder[0].id
        } else return folderId
    }, [folders, folderId])

    const blogResult = useBlogData(selectedCategoryId, {
        limit: sz,
        offset: start,
        sort,
        enable: !!selectedCategoryId
    })
    const {data: blogData, isLoading: loading, error, isError} = blogResult || {}
    const paramName = selectedCategoryId?.split('-')?.at(-1)
    const isMobile = useBreakpointValue({base: true, lg: false})

    useEffect(() => {
        if (isError) {
            let errorBody
            const getError = async () => {
                if (error?.response instanceof Response) {
                    try {
                        errorBody = await error?.response?.clone()?.json()
                    } catch {
                        const text = await error?.response?.text()
                        try {
                            errorBody = JSON?.parse(text)
                        } catch {
                            errorBody = {message: text}
                        }
                    }
                }
                if (errorBody?.errorCode === 'SYS-1014') {
                    showToast({
                        title: errorBody?.error?.message,
                        status: 'error',
                        position: 'top-right'
                    })
                }
            }
            getError()
        }
    }, [error, isError])

    useEffect(() => {
        setIsOpenAllCAtegoryList(false)
    }, [loading, blogData])

    if ((loading && !error) || !blogData) {
        return (
            <>
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        zIndex: '99999999',
                        height: '100vh',
                        width: '100vw',
                        background: 'rgba(0, 0, 0, 0.4)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <Spinner
                        data-testid="loading"
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="gray.200"
                        color="blue.600"
                        size="xl"
                    />
                </Box>
                <Box minHeight={'700px'}></Box>
            </>
        )
    }

    if (!blogData && !loading) {
        return (
            <Box
                height={'60vh'}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                fontFamily={pxToRem(14)}
            >
                <FormattedMessage
                    defaultMessage={`No data found`}
                    id="blog_landing.breadcrumb.not_found_no_data_msg"
                />
            </Box>
        )
    }
    const {
        data: {heroImage, mainCategories, featuredArticle, popularPosts, articles, maxPages}
    } = blogData

    const handleArchiveFilterChange = (filters) => {
        setArchiveFilters(filters)
    }

    return (
        <Box sx={BlogPageStyle}>
            <Seo
                title={rest?.page_title}
                description={rest?.page_description}
                keywords={rest?.page_keywords}
            />
            <Box margin={`${pxToRem(15)} ${pxToRem(10)}`} px={pxToRem(20)} pt={pxToRem(16)}>
                <Link
                    to={blogRoute('cavenders-ranch')}
                    as={RouteLink}
                    fontSize={pxToRem(14)}
                    fontFamily={fonts.primary}
                    fontStyle={folderId === 'cavenders-ranch' && 'italic'}
                    fontWeight={600}
                    lineHeight={{base: pxToRem(20), lg: pxToRem(14)}}
                    letterSpacing={pxToRem(4)}
                    textTransform={'uppercase'}
                    color={'black'}
                    _hover={{
                        textDecoration: 'none',
                        color: 'cav_red_separator'
                    }}
                >
                    <FormattedMessage
                        defaultMessage={`Cavender's Ranch`}
                        id="blog_landing.breadcrumb.label.cavenders_ranch"
                    />
                </Link>
                {folderId !== 'cavenders-ranch' && (
                    <Text as="span">
                        {' '}
                        <Text
                            as="span"
                            color="cav_border_gray"
                            fontStyle="normal"
                            fontSize={pxToRem(14)}
                            fontWeight={600}
                            px={pxToRem(5)}
                        >
                            {'>'}
                        </Text>{' '}
                        <Link
                            to={folderId === 'archive' ? blogRoute('archive') : blogRoute(folderId)}
                            as={RouteLink}
                            fontSize={pxToRem(14)}
                            fontFamily={fonts.primary}
                            fontStyle={
                                folderId === 'archive' &&
                                (archiveFilters.selectedYear || archiveFilters.selectedMonth)
                                    ? 'normal'
                                    : 'italic'
                            }
                            fontWeight={600}
                            lineHeight={{base: pxToRem(20), lg: pxToRem(14)}}
                            letterSpacing={pxToRem(4)}
                            textTransform={'uppercase'}
                            color={'black'}
                            _hover={{
                                textDecoration: 'none',
                                color: 'cav_red_separator'
                            }}
                        >
                            {paramName}
                        </Link>
                    </Text>
                )}
                {folderId === 'archive' && archiveFilters.selectedYear && (
                    <Text as="span">
                        {' '}
                        <Text
                            as="span"
                            color="cav_border_gray"
                            fontStyle="normal"
                            fontSize={pxToRem(14)}
                            fontWeight={600}
                            px={pxToRem(5)}
                        >
                            {'>'}
                        </Text>{' '}
                        <Link
                            to={`${blogRoute('archive')}?year=${archiveFilters.selectedYear}`}
                            as={RouteLink}
                            fontSize={pxToRem(14)}
                            fontFamily={fonts.primary}
                            fontStyle={archiveFilters.selectedMonth ? 'normal' : 'italic'}
                            fontWeight={600}
                            lineHeight={{base: pxToRem(20), lg: pxToRem(14)}}
                            letterSpacing={pxToRem(4)}
                            textTransform={'uppercase'}
                            color={'black'}
                            _hover={{
                                textDecoration: 'none',
                                color: 'cav_red_separator'
                            }}
                        >
                            {archiveFilters.selectedYear}
                        </Link>
                    </Text>
                )}
                {folderId === 'archive' &&
                    archiveFilters.selectedMonth &&
                    archiveFilters.selectedYear && (
                        <Text
                            as="span"
                            fontSize={pxToRem(14)}
                            fontFamily={fonts.primary}
                            fontStyle={'italic'}
                            fontWeight={600}
                            lineHeight={{base: pxToRem(20), lg: pxToRem(14)}}
                            letterSpacing={pxToRem(4)}
                            textTransform={'uppercase'}
                            color={'black'}
                            _hover={{
                                textDecoration: 'none',
                                color: 'cav_red_separator',
                                cursor: 'pointer'
                            }}
                        >
                            {' '}
                            <Text
                                as="span"
                                color="cav_border_gray"
                                fontStyle="normal"
                                fontSize={pxToRem(14)}
                                fontWeight={600}
                            >
                                {'>'}
                            </Text>{' '}
                            {archiveFilters.selectedMonth}
                        </Text>
                    )}
            </Box>
            <Box as={'h2'} className="blog-banner" margin={`${pxToRem(24)} 0`} textAlign={'center'}>
                <Link to={blogRoute('cavenders-ranch')} as={RouteLink} title="Cavender's Ranch">
                    <Image src={heroImage} alt="Cavenders Ranch Blog" />
                    <Text
                        as="span"
                        fontSize={pxToRem(30)}
                        lineHeight={pxToRem(36)}
                        textAlign={'center'}
                        color={'cav_light_black'}
                        fontFamily={fonts.primary}
                        marginBottom={0}
                        letterSpacing={pxToRem(1)}
                        textTransform={'uppercase'}
                        fontWeight={700}
                    >
                        <FormattedMessage
                            defaultMessage={`Cavender's Ranch`}
                            id="blog_landing.label.cavenders_ranch"
                        />
                    </Text>
                </Link>
            </Box>
            <Box
                display={'flex'}
                gap={pxToRem(4)}
                justifyContent={'center'}
                margin={`${pxToRem(25)} ${pxToRem(20)}`}
                flexDirection={{
                    base: 'column',
                    lg: 'row'
                }}
                position={'relative'}
            >
                <Box
                    as="button"
                    fontSize={pxToRem(14)}
                    lineHeight={pxToRem(34)}
                    textAlign="center"
                    color="white"
                    fontFamily={fonts.primary}
                    marginBottom={0}
                    letterSpacing={pxToRem(1)}
                    textTransform="uppercase"
                    whiteSpace="nowrap"
                    backgroundColor="cav_red_separator"
                    fontWeight={600}
                    flex={1}
                    borderWidth={pxToRem(1)}
                    borderColor="cav_red_separator"
                    borderRadius={0}
                    onMouseEnter={!isMobile ? () => setIsOpenAllCAtegoryList(true) : undefined}
                    onMouseLeave={!isMobile ? () => setIsOpenAllCAtegoryList(false) : undefined}
                    onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                            setIsOpenAllCAtegoryList(false)
                        }
                    }}
                    onClick={() => setIsOpenAllCAtegoryList((preState) => !preState)}
                    _hover={{
                        textDecoration: 'none',
                        color: 'cav_red_separator',
                        backgroundColor: 'white',
                        borderColor: 'cav_red_separator'
                    }}
                    _focus={{
                        outline: '2px solid cav_blue_outline',
                        outlineOffset: '2px',
                        boxShadow: 'none'
                    }}
                >
                    <FormattedMessage
                        defaultMessage="All Categories"
                        id="blog_landing.label.all_categories"
                    />

                    {isOpenAllCAtegoryList && (
                        <Box
                            position="absolute"
                            top={pxToRem(36)}
                            left={0}
                            minWidth={pxToRem(200)}
                            border={'1px solid'}
                            borderColor={'cav_pdp_light_gray'}
                            padding={pxToRem(8)}
                            zIndex={100}
                            backgroundColor={'white'}
                            width={{base: '100%', lg: 'initial'}}
                        >
                            {folders &&
                                folders.map((folder) => (
                                    <Box
                                        key={folder.id}
                                        onClick={() => setIsOpenAllCAtegoryList(false)}
                                    >
                                        <Link
                                            to={blogRoute(folder.id)}
                                            as={RouteLink}
                                            fontSize={pxToRem(14)}
                                            lineHeight={pxToRem(18)}
                                            letterSpacing={pxToRem(1)}
                                            color={'black'}
                                            fontFamily={fonts.primary}
                                            fontWeight={600}
                                            textTransform={'uppercase'}
                                            textAlign={'left'}
                                            display={'inherit'}
                                            _hover={{
                                                textDecoration: 'none',
                                                color: 'cav_red_separator'
                                            }}
                                        >
                                            {folder.name}
                                        </Link>
                                    </Box>
                                ))}
                        </Box>
                    )}
                </Box>

                {mainCategories.map((category) => {
                    return (
                        <Link
                            to={blogRoute(category.id)}
                            as={RouteLink}
                            key={category.id}
                            fontSize={pxToRem(14)}
                            lineHeight={pxToRem(34)}
                            textAlign={'center'}
                            color={'white'}
                            fontFamily={fonts.primary}
                            marginBottom={0}
                            letterSpacing={pxToRem(1)}
                            textTransform={'uppercase'}
                            whiteSpace={'nowrap'}
                            backgroundColor={'cav_red_separator'}
                            fontWeight={600}
                            borderWidth={pxToRem(1)}
                            borderColor={'cav_red_separator'}
                            borderRadius={0}
                            _hover={{
                                textDecoration: 'none',
                                color: 'cav_red_separator',
                                backgroundColor: 'white',
                                border: '1px solid cav_red_separator'
                            }}
                            flex={1}
                        >
                            {category.name}
                        </Link>
                    )
                })}
                <Link
                    to={blogRoute('archive')}
                    as={RouteLink}
                    fontSize={pxToRem(14)}
                    lineHeight={pxToRem(34)}
                    textAlign={'center'}
                    color={'white'}
                    fontFamily={fonts.primary}
                    marginBottom={0}
                    letterSpacing={pxToRem(1)}
                    textTransform={'uppercase'}
                    whiteSpace={'nowrap'}
                    backgroundColor={'cav_red_separator'}
                    fontWeight={600}
                    borderWidth={pxToRem(1)}
                    borderColor={'cav_red_separator'}
                    borderRadius={0}
                    _hover={{
                        textDecoration: 'none',
                        color: 'cav_red_separator',
                        backgroundColor: 'white',
                        border: '1px solid cav_red_separator'
                    }}
                    flex={1}
                >
                    <FormattedMessage defaultMessage={`Archive`} id="blog_landing.label.archive" />
                </Link>
            </Box>
            {folderId === 'archive' ? (
                <BlogArchive onFilterChange={handleArchiveFilterChange} />
            ) : (
                <>
                    <Box>
                        <FeaturedArticle featuredArticle={featuredArticle} />
                    </Box>
                    <Box
                        display={'flex'}
                        flexDirection={{base: 'column', lg: 'row'}}
                        paddingBottom={pxToRem(20)}
                    >
                        <Box
                            as="section"
                            boxSizing={'border-box'}
                            margin={'0 20px 20px'}
                            flex={3}
                            w={{base: 'inherit', lg: 'auto'}}
                        >
                            <ArticlesPagination maxPages={maxPages} />
                            {articles?.map((article) => (
                                <Articles key={article.id} article={article} />
                            ))}
                        </Box>
                        <Box
                            as="aside"
                            padding={`0 ${pxToRem(20)}`}
                            flex={1}
                            w={{base: '100%', lg: 'auto'}}
                            borderLeft={'1px solid'}
                            borderColor={'cav_pdp_light_gray'}
                        >
                            <Text
                                fontSize={pxToRem(18)}
                                lineHeight={pxToRem(24)}
                                letterSpacing={pxToRem(1)}
                                textAlign={'center'}
                                color={'cav_light_black'}
                                fontFamily={fonts.primary}
                                marginTop={pxToRem(5)}
                                marginBottom={pxToRem(15)}
                                textTransform={'uppercase'}
                                whiteSpace={'nowrap'}
                                fontWeight={400}
                            >
                                <FormattedMessage
                                    defaultMessage={`Popular Posts`}
                                    id="blog_landing.label.popularPosts"
                                />
                            </Text>
                            {popularPosts?.map((popularPost) => (
                                <PopularPosts key={popularPost.id} popularPost={popularPost} />
                            ))}
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    )
}

export default BlogLanding
